import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { useI18n } from '@/lib/i18n'
import { ChevronLeft, ChevronRight, StickyNote } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY_PREFIX = 'mentiss_play_notes_'

interface DraftNotesProps {
  gameId: string
  collapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
  showCollapseButton?: boolean
}

export function DraftNotes({
  gameId,
  collapsed = false,
  onCollapsedChange,
  showCollapseButton = true,
}: DraftNotesProps) {
  const { t } = useI18n()
  const [value, setValue] = useState('')

  useEffect(() => {
    if (!gameId)
      return
    try {
      const key = `${STORAGE_KEY_PREFIX}${gameId}`
      const stored = localStorage.getItem(key)
      setValue(stored ?? '')
    }
    catch {
      setValue('')
    }
  }, [gameId])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const next = e.target.value
      setValue(next)
      if (!gameId)
        return
      try {
        localStorage.setItem(`${STORAGE_KEY_PREFIX}${gameId}`, next)
      }
      catch {}
    },
    [gameId],
  )

  if (collapsed) {
    return (
      <Card
        className="flex min-h-0 flex-1 flex-col items-center gap-3 border-border/50 bg-background/50 px-1 py-3 backdrop-blur-sm"
        data-testid="draft-notes-collapsed"
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label={t('play.expandNotes')}
          title={t('play.expandNotes')}
          data-testid="expand-notes-button"
          onClick={() => onCollapsedChange?.(false)}
        >
          <ChevronRight className="size-4" />
        </Button>
        <StickyNote className="size-5 text-primary" aria-hidden="true" />
      </Card>
    )
  }

  return (
    <Card className="flex min-h-0 flex-1 flex-col border-border/50 bg-background/50 backdrop-blur-sm">
      <CardHeader className="flex shrink-0 flex-row items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2 text-lg font-medium">
          <StickyNote className="size-5 text-primary" />
          {t('play.draftNotes')}
        </CardTitle>
        {showCollapseButton && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label={t('play.collapseNotes')}
            title={t('play.collapseNotes')}
            data-testid="collapse-notes-button"
            onClick={() => onCollapsedChange?.(true)}
          >
            <ChevronLeft className="size-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-2 pt-0">
        <Textarea
          placeholder={t('play.draftNotesPlaceholder')}
          value={value}
          onChange={handleChange}
          className="min-h-0 flex-1 resize-none text-sm"
          data-saves-as-you-type=""
        />
      </CardContent>
    </Card>
  )
}
